import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ChevronLeft,
  ChevronRight,
  KeyboardArrowDown,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Security as SecurityIcon,
  Public as PublicIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import roleService, { Role, CreateRoleDto, UpdateRoleDto } from '../services/roleService';
import permissionService, {
  Permission,
  PermissionCategory,
  permissionExpansionAPI,
  ExpansionResult,
} from '../services/permissionService';
import {
  PERMISSION_CATALOG,
  PermissionMeta,
} from '../constants/permissionCatalog';
import tenantService, { Tenant } from '../services/tenantService';
// Session 3 cutover: PermissionButton/PermissionGuard/MENU_PERMISSION_MAP are gone.
// Use <Gate> (../components/access/Gate) and the catalog-driven adminMenus instead.
import Gate from '../components/access/Gate';
import RolePermissionPicker from '../components/access/RolePermissionPicker';
import EntityHistoryButton from '../components/common/EntityHistoryButton';
import { ConfirmDialog } from '../components/common';
import './EntityManagement.css';
import './RoleManagement.css';

const RoleManagement: React.FC = () => {
  // Data states
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [permissionsByCategory, setPermissionsByCategory] = useState<PermissionCategory[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingTenants, setLoadingTenants] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState<number>(0);

  // Dialog states
  const [openCreateDialog, setOpenCreateDialog] = useState<boolean>(false);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [openViewDialog, setOpenViewDialog] = useState<boolean>(false);
  const [dialogLoading, setDialogLoading] = useState<boolean>(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  // Pagination & search
  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [roleTypeFilter, setRoleTypeFilter] = useState<string>('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Form states
  const [newRole, setNewRole] = useState<CreateRoleDto & { tenantId?: string; roleType?: 'global' | 'tenant' }>({
    name: '',
    description: '',
    permissions: [],
    roleType: 'global'
  });
  const [editRole, setEditRole] = useState<Role | null>(null);
  const [editRoleType, setEditRoleType] = useState<'global' | 'tenant'>('global');
  const [editRoleTenantId, setEditRoleTenantId] = useState<string | undefined>(undefined);

  // Delete & view
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [viewRole, setViewRole] = useState<Role | null>(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', type: 'success' as 'success' | 'error' });
  const notify = (msg: string, type: 'success' | 'error') => setSnackbar({ open: true, msg, type });

  // Live dependency expansion preview — debounced. Always reflects the CURRENT
  // open dialog's picks (create OR edit, whichever is open).
  const [expansion, setExpansion] = useState<ExpansionResult | null>(null);
  const [expansionLoading, setExpansionLoading] = useState(false);
  const activePicks = openCreateDialog
    ? newRole.permissions ?? []
    : openEditDialog
      ? editRole?.permissions ?? []
      : [];
  useEffect(() => {
    // Wildcard role has no expansion concept
    if (activePicks.length === 1 && activePicks[0] === '*') {
      setExpansion(null);
      return;
    }
    if (activePicks.length === 0) {
      setExpansion(null);
      return;
    }
    let cancelled = false;
    setExpansionLoading(true);
    const t = setTimeout(() => {
      permissionExpansionAPI
        .expand(activePicks)
        .then((res) => {
          if (!cancelled) setExpansion(res);
        })
        .catch(() => {
          if (!cancelled) setExpansion(null);
        })
        .finally(() => {
          if (!cancelled) setExpansionLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePicks.join('|')]);

  const getId = (role: Role) => role.id || (role as any)._id || '';

  // Fetch roles
  const fetchRoles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await roleService.getRoles(page, limit, searchTerm);
      if (response.items && Array.isArray(response.items)) {
        setRoles(response.items);
        setTotalItems(response.meta.totalItems);
      }
    } catch (err) {
      console.error('Error in fetchRoles:', err);
      setError('خطا در دریافت اطلاعات نقش‌ها');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchTerm]);

  // Fetch permissions
  const fetchPermissions = useCallback(async () => {
    try {
      const allPermissions = await permissionService.getAllPermissions();
      setPermissions(allPermissions || []);
      try {
        const categorizedPermissions = await permissionService.getPermissionsByCategory();
        setPermissionsByCategory(categorizedPermissions || []);
      } catch {
        setPermissionsByCategory([]);
      }
    } catch {
      setPermissions([]);
      setPermissionsByCategory([]);
    }
  }, []);

  // Fetch tenants
  const fetchTenants = useCallback(async () => {
    setLoadingTenants(true);
    try {
      const response = await tenantService.getTenants(0, 100);
      setTenants(response.items);
    } catch {
      console.error('خطا در دریافت اطلاعات تنانت‌ها');
    } finally {
      setLoadingTenants(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
    fetchTenants();
  }, [fetchRoles, fetchPermissions, fetchTenants]);

  // Create role
  const handleCreateRole = async () => {
    setDialogLoading(true);
    setDialogError(null);
    try {
      if (newRole.roleType === 'tenant' && !newRole.tenantId) {
        setDialogError('لطفا یک تنانت انتخاب کنید');
        setDialogLoading(false);
        return;
      }

      // newRole.permissions is the picks list (UI state name predates the rename);
      // we send it as permissionPicks so the backend expands via the catalog.
      const roleData = {
        name: newRole.name,
        description: newRole.description,
        permissionPicks: newRole.permissions ?? []
      };

      if (newRole.roleType === 'global') {
        await roleService.createGlobalRole(roleData);
      } else {
        if (!newRole.tenantId) {
          setDialogError('لطفا یک تنانت انتخاب کنید');
          setDialogLoading(false);
          return;
        }
        await roleService.createTenantRole({ ...roleData, tenantId: newRole.tenantId });
      }

      setOpenCreateDialog(false);
      setNewRole({ name: '', description: '', permissions: [], roleType: 'global' });
      notify('نقش جدید ایجاد شد', 'success');
      fetchRoles();
    } catch (err) {
      setDialogError('خطا در ایجاد نقش جدید');
      console.error(err);
    } finally {
      setDialogLoading(false);
    }
  };

  // Edit role
  const handleEditRole = async () => {
    if (!editRole) {
      setDialogError('اطلاعات نقش در دسترس نیست');
      return;
    }
    if (!editRole.id) {
      setDialogError('شناسه نقش نامعتبر است');
      return;
    }
    if (!editRole.name || editRole.name.trim() === '') {
      setDialogError('نام نقش نمی‌تواند خالی باشد');
      return;
    }

    setDialogLoading(true);
    setDialogError(null);
    try {
      // editRole.permissions holds the picks (UI legacy name); send as
      // permissionPicks so backend re-expands via the catalog.
      const updateData: UpdateRoleDto = {
        name: editRole.name.trim(),
        description: editRole.description?.trim() || '',
        permissionPicks: Array.isArray(editRole.permissions) ? editRole.permissions : []
      };

      await roleService.updateRole(editRole.id, updateData);
      setOpenEditDialog(false);
      notify('نقش بروزرسانی شد', 'success');
      fetchRoles();
    } catch (err: any) {
      setDialogError(err.message || 'خطا در ویرایش نقش');
    } finally {
      setDialogLoading(false);
    }
  };

  // Delete role
  const handleDeleteRole = async () => {
    if (!selectedRoleId) return;
    setDialogLoading(true);
    setDialogError(null);
    try {
      await roleService.deleteRole(selectedRoleId);
      setOpenDeleteDialog(false);
      setSelectedRoleId(null);
      notify('نقش حذف شد', 'success');
      fetchRoles();
    } catch (err) {
      setDialogError('خطا در حذف نقش');
      console.error(err);
    } finally {
      setDialogLoading(false);
    }
  };

  // Open edit dialog
  const handleOpenEditDialog = (role: Role) => {
    if (!role || !role.id) {
      notify('نقش نامعتبر', 'error');
      return;
    }
    // Seed the picker from picks when available (post-migration), else fall back
    // to the full permissions list (which the backend will re-treat as picks).
    const initialPicks =
      Array.isArray(role.permissionPicks) && role.permissionPicks.length > 0
        ? role.permissionPicks
        : Array.isArray(role.permissions)
          ? role.permissions
          : [];
    const roleCopy = {
      ...role,
      id: role.id,
      permissions: [...initialPicks],
      description: role.description || ''
    };
    setEditRole(roleCopy);
    const isTenantRole = role.tenantId !== undefined && role.tenantId !== null;
    setEditRoleType(isTenantRole ? 'tenant' : 'global');
    setEditRoleTenantId(role.tenantId === null ? undefined : role.tenantId);
    setDialogError(null);
    setOpenEditDialog(true);
  };

  // Permission helpers
  const isCategoryFullySelected = (categoryPermissions: Permission[], selectedPermissions: string[]) => {
    return categoryPermissions.every(p => selectedPermissions.includes(p.id));
  };

  const isCategoryPartiallySelected = (categoryPermissions: Permission[], selectedPermissions: string[]) => {
    return categoryPermissions.some(p => selectedPermissions.includes(p.id)) &&
      !isCategoryFullySelected(categoryPermissions, selectedPermissions);
  };

  const toggleCategoryPermissions = (categoryPermissions: Permission[], selectedPermissions: string[], setter: Function) => {
    const isFullySelected = isCategoryFullySelected(categoryPermissions, selectedPermissions);
    if (isFullySelected) {
      setter((prev: any) => ({
        ...prev,
        permissions: selectedPermissions.filter(id => !categoryPermissions.some(p => p.id === id))
      }));
    } else {
      const newPermissions = [...selectedPermissions];
      categoryPermissions.forEach(permission => {
        if (!newPermissions.includes(permission.id)) {
          newPermissions.push(permission.id);
        }
      });
      setter((prev: any) => ({ ...prev, permissions: newPermissions }));
    }
  };

  // Permission list display for table
  const renderPermissionChips = (perms: string[]) => {
    if (!perms || perms.length === 0) {
      return <span style={{ color: 'var(--color-text-disabled)', fontSize: '0.8125rem' }}>بدون دسترسی</span>;
    }
    if (perms.length === 1 && perms[0] === '*') {
      return <span className="tag tag-sm tag-full-access">دسترسی کامل</span>;
    }
    const displayCount = 2;
    const displayPerms = perms.slice(0, displayCount);
    const remaining = perms.length - displayCount;
    return (
      <div className="permissions-cell">
        {displayPerms.map((perm, i) => (
          <span key={i} className="tag tag-xs tag-permission">{perm}</span>
        ))}
        {remaining > 0 && (
          <Tooltip title={perms.slice(displayCount).join(', ')}>
            <span className="tag tag-xs tag-more">+{remaining} مورد</span>
          </Tooltip>
        )}
      </div>
    );
  };

  /**
   * Live preview panel: shows which permissions will be auto-included because
   * of the user's current picks, and which pick(s) caused each inclusion.
   * Rendered below the picker in both Create and Edit dialogs.
   */
  const renderExpansionPreview = () => {
    if (activePicks.length === 1 && activePicks[0] === '*') {
      return (
        <div className="perm-expansion-panel perm-expansion-wildcard">
          <strong>دسترسی کامل (*)</strong> — این نقش به همه چیز دسترسی دارد. هیچ
          وابستگی محاسبه نمی‌شود.
        </div>
      );
    }
    if (activePicks.length === 0) return null;
    if (expansionLoading && !expansion) {
      return (
        <div className="perm-expansion-panel perm-expansion-loading">
          در حال محاسبه وابستگی‌ها...
        </div>
      );
    }
    if (!expansion) return null;
    const auto = expansion.autoIncluded ?? [];
    const picksCount = expansion.picks?.length ?? activePicks.length;
    const expandedCount = expansion.expanded?.length ?? picksCount;
    const breakdown = expansion.breakdown ?? [];
    if (auto.length === 0) {
      return (
        <div className="perm-expansion-panel perm-expansion-empty">
          ✓ هیچ دسترسی خودکار اضافی نیاز نیست. {picksCount} انتخاب شما ={' '}
          {expandedCount} دسترسی موثر.
        </div>
      );
    }
    return (
      <div className="perm-expansion-panel">
        <div className="perm-expansion-header">
          <span>
            انتخاب شما: <strong>{picksCount}</strong>
          </span>
          <span>
            خودکار اضافه می‌شود: <strong>{auto.length}</strong>
          </span>
          <span>
            مجموع موثر: <strong>{expandedCount}</strong>
          </span>
          {expansionLoading && <span style={{ opacity: 0.6 }}>(در حال بروزرسانی...)</span>}
        </div>
        <div className="perm-expansion-list">
          {auto.map((perm) => {
            const reasons = breakdown.find((b) => b.permission === perm)?.pulledInBy ?? [];
            const meta = (PERMISSION_CATALOG as Record<string, PermissionMeta>)[perm];
            const label = meta?.nameFa || perm;
            const reasonLabels = reasons.map((r) => {
              const m = (PERMISSION_CATALOG as Record<string, PermissionMeta>)[r];
              return m?.nameFa || r;
            });
            return (
              <div key={perm} className="perm-expansion-item">
                <span className="perm-expansion-code">{label}</span>
                <span className="perm-expansion-reason">
                  ← به دلیل: {reasonLabels.length > 0 ? reasonLabels.join('، ') : '—'}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Categorized permissions render for dialogs
  const renderCategorizedPermissions = (selectedPermissions: string[], setter: Function) => {
    const effectiveCategories = permissionsByCategory.length > 0
      ? permissionsByCategory
      : permissions.length > 0
        ? [{ name: 'همه دسترسی‌ها', permissions: permissions }]
        : [];

    return (
      <>
        {permissions.length === 0 ? (
          <div className="perm-loading">در حال بارگذاری دسترسی‌ها...</div>
        ) : (
          effectiveCategories.map((category) => (
            <div key={category.name} className="perm-category">
              <label className="perm-category-header" onClick={(e) => e.preventDefault()}>
                <input
                  type="checkbox"
                  checked={isCategoryFullySelected(category.permissions, selectedPermissions)}
                  ref={el => {
                    if (el) el.indeterminate = isCategoryPartiallySelected(category.permissions, selectedPermissions);
                  }}
                  onChange={() => toggleCategoryPermissions(category.permissions, selectedPermissions, setter)}
                />
                <span className="perm-category-name">{category.name}</span>
                <span className="perm-category-count">({category.permissions.length})</span>
              </label>
              <div className="perm-category-body">
                {category.permissions.map((permission) => (
                  <label key={permission.id} className="perm-item" title={permission.description || permission.name}>
                    <input
                      type="checkbox"
                      checked={selectedPermissions.includes(permission.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setter((prev: any) => ({ ...prev, permissions: [...prev.permissions, permission.id] }));
                        } else {
                          setter((prev: any) => ({ ...prev, permissions: prev.permissions.filter((id: string) => id !== permission.id) }));
                        }
                      }}
                    />
                    <span className="perm-item-name">{permission.name}</span>
                  </label>
                ))}
              </div>
            </div>
          ))
        )}

        <div className="perm-actions">
          <button
            className="perm-action-btn clear"
            type="button"
            onClick={() => setter((prev: any) => ({ ...prev, permissions: [] }))}
          >
            حذف همه
          </button>
          <button
            className="perm-action-btn select-all"
            type="button"
            onClick={() => {
              const allPermissionIds = permissions.map(p => p.id);
              setter((prev: any) => ({ ...prev, permissions: allPermissionIds }));
            }}
          >
            انتخاب همه
          </button>
          <button
            className="perm-action-btn full-access"
            type="button"
            onClick={() => setter((prev: any) => ({ ...prev, permissions: ['*'] }))}
          >
            دسترسی کامل (*)
          </button>
        </div>
      </>
    );
  };

  // Filtered roles
  const filteredRoles = roleTypeFilter
    ? roles.filter(r => {
        if (roleTypeFilter === 'global') return !r.tenantId;
        if (roleTypeFilter === 'tenant') return !!r.tenantId;
        return true;
      })
    : roles;

  // Stats
  const totalRoles = totalItems;
  const globalRoles = roles.filter(r => !r.tenantId).length;
  const tenantRoles = roles.filter(r => !!r.tenantId).length;

  const totalPages = Math.ceil(totalItems / limit);
  const selectedRoleName = roles.find(r => getId(r) === selectedRoleId)?.name || '';

  return (
    <div className="roles-page">
      {/* Header */}
      <header className="users-header">
        <div>
          <h1 className="users-title">مدیریت نقش‌ها</h1>
          <p className="header-subtitle">مدیریت نقش‌ها و دسترسی‌ها</p>
        </div>
        <div className="header-actions">
          <button className="header-btn refresh" onClick={() => { fetchRoles(); fetchPermissions(); }} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
          <Gate permission="admin_role_create">
            <button className="header-btn primary" onClick={() => { setDialogError(null); setOpenCreateDialog(true); }}>
              <AddIcon fontSize="small" />
              <span>نقش جدید</span>
            </button>
          </Gate>
        </div>
      </header>

      {/* Stats */}
      <div className="users-stats">
        <div className="users-stat-card">
          <div className="users-stat-card-inner">
            <div className="users-stat-card-content">
              <div className="users-stat-icon blue"><SecurityIcon /></div>
              <div>
                <p className="users-stat-label">کل نقش‌ها</p>
                <p className="users-stat-value">{totalRoles}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="users-stat-card">
          <div className="users-stat-card-inner">
            <div className="users-stat-card-content">
              <div className="users-stat-icon green"><PublicIcon /></div>
              <div>
                <p className="users-stat-label">نقش‌های سراسری</p>
                <p className="users-stat-value">{globalRoles}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="users-stat-card">
          <div className="users-stat-card-inner">
            <div className="users-stat-card-content">
              <div className="users-stat-icon orange"><BusinessIcon /></div>
              <div>
                <p className="users-stat-label">نقش‌های تنانت</p>
                <p className="users-stat-value">{tenantRoles}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="جستجو در نقش‌ها..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && fetchRoles()}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">نوع نقش</label>
            <select
              className="filter-field"
              value={roleTypeFilter}
              onChange={e => { setRoleTypeFilter(e.target.value); setPage(0); }}
            >
              <option value="">همه نقش‌ها</option>
              <option value="global">سراسری</option>
              <option value="tenant">مختص تنانت</option>
            </select>
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={() => fetchRoles()}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {(searchTerm || roleTypeFilter) && (
              <button className="filter-btn outline" onClick={() => { setSearchTerm(''); setRoleTypeFilter(''); setPage(0); }}>
                پاک
              </button>
            )}
          </div>
        </div>
        {(searchTerm || roleTypeFilter) && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {searchTerm && (
              <span className="filter-tag">
                جستجو: {searchTerm}
                <button onClick={() => { setSearchTerm(''); }}>×</button>
              </span>
            )}
            {roleTypeFilter && (
              <span className="filter-tag">
                نوع: {roleTypeFilter === 'global' ? 'سراسری' : 'تنانت'}
                <button onClick={() => { setRoleTypeFilter(''); }}>×</button>
              </span>
            )}
            <button className="filters-clear" onClick={() => { setSearchTerm(''); setRoleTypeFilter(''); setPage(0); }}>
              پاک کردن همه
            </button>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="error-alert">
          {error}
        </div>
      )}

      {/* Content */}
      <div className="users-table-wrapper">
        {loading ? (
          <div className="users-loading"><CircularProgress size={36} /></div>
        ) : filteredRoles.length === 0 ? (
          <div className="users-empty">
            <SecurityIcon className="users-empty-icon" />
            <p className="users-empty-text">نقشی یافت نشد</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <table className="users-table">
              <thead>
                <tr>
                  <th>نقش</th>
                  <th>نوع</th>
                  <th>تنانت</th>
                  <th>دسترسی‌ها</th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {filteredRoles.map(role => {
                  const safeRoleId = getId(role);
                  return (
                    <tr key={safeRoleId}>
                      <td>
                        <div className="role-info-cell">
                          <span className="role-name">{role.name}</span>
                          {role.description && <span className="role-description">{role.description}</span>}
                        </div>
                      </td>
                      <td>
                        {role.tenantId ? (
                          <span className="tag tag-sm tag-tenant">تنانت</span>
                        ) : (
                          <span className="tag tag-sm tag-global">سراسری</span>
                        )}
                      </td>
                      <td>
                        {role.tenantId
                          ? tenants.find(t => t.id === role.tenantId)?.name || 'تنانت ناشناخته'
                          : '-'
                        }
                      </td>
                      <td>
                        {renderPermissionChips(role.permissions)}
                        <span className="permission-count">({role.permissions?.length || 0} دسترسی)</span>
                      </td>
                      <td>
                        <div className="actions-cell">
                          <Gate permission="admin_role_read">
                            <Tooltip title="مشاهده دسترسی‌ها">
                              <button
                                className="action-btn view"
                                onClick={() => { setViewRole(role); setOpenViewDialog(true); }}
                              >
                                <ViewIcon fontSize="small" />
                              </button>
                            </Tooltip>
                          </Gate>
                          <EntityHistoryButton
                            entityType="Role"
                            entityId={safeRoleId}
                            size="small"
                            variant="text"
                          />
                          <Gate permission="admin_role_update">
                            <Tooltip title="ویرایش">
                              <button
                                className="action-btn edit"
                                onClick={() => handleOpenEditDialog(role)}
                              >
                                <EditIcon fontSize="small" />
                              </button>
                            </Tooltip>
                          </Gate>
                          <Gate permission="admin_role_delete">
                            <Tooltip title="حذف">
                              <button
                                className="action-btn delete"
                                onClick={() => { setSelectedRoleId(safeRoleId); setDialogError(null); setOpenDeleteDialog(true); }}
                              >
                                <DeleteIcon fontSize="small" />
                              </button>
                            </Tooltip>
                          </Gate>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="users-cards">
              {filteredRoles.map(role => {
                const safeRoleId = getId(role);
                const isOpen = expandedId === safeRoleId;
                return (
                  <div key={safeRoleId} className="role-card">
                    <div className="role-card-header" onClick={() => setExpandedId(isOpen ? null : safeRoleId)}>
                      <div className="role-card-icon">
                        <SecurityIcon />
                      </div>
                      <div className="role-card-info">
                        <div className="role-card-name">
                          {role.name}
                          {role.tenantId ? (
                            <span className="tag tag-xs tag-tenant">تنانت</span>
                          ) : (
                            <span className="tag tag-xs tag-global">سراسری</span>
                          )}
                        </div>
                        {role.description && <div className="role-card-desc">{role.description}</div>}
                      </div>
                      <div className="role-card-status">
                        <span className="permission-count">{role.permissions?.length || 0} دسترسی</span>
                        <button className={`role-card-toggle ${isOpen ? 'open' : ''}`}>
                          <KeyboardArrowDown fontSize="small" />
                        </button>
                      </div>
                    </div>

                    <div className={`role-card-details ${isOpen ? 'open' : ''}`}>
                      <div className="role-card-row">
                        <span className="role-card-label">نوع نقش</span>
                        <span className="role-card-value">
                          {role.tenantId ? 'مختص تنانت' : 'سراسری'}
                        </span>
                      </div>
                      {role.tenantId && (
                        <div className="role-card-row">
                          <span className="role-card-label">تنانت</span>
                          <span className="role-card-value">
                            {tenants.find(t => t.id === role.tenantId)?.name || 'نامشخص'}
                          </span>
                        </div>
                      )}
                      <div className="role-card-row">
                        <span className="role-card-label">دسترسی‌ها</span>
                        <div className="role-card-permissions">
                          {renderPermissionChips(role.permissions)}
                        </div>
                      </div>

                      <div className="role-card-actions">
                        <Gate permission="admin_role_read">
                          <button className="role-card-btn" onClick={() => { setViewRole(role); setOpenViewDialog(true); }}>
                            <ViewIcon fontSize="small" /> مشاهده
                          </button>
                        </Gate>
                        <EntityHistoryButton entityType="Role" entityId={safeRoleId} buttonLabel="تاریخچه" size="small" />
                        <Gate permission="admin_role_update">
                          <button className="role-card-btn primary" onClick={() => handleOpenEditDialog(role)}>
                            <EditIcon fontSize="small" /> ویرایش
                          </button>
                        </Gate>
                        <Gate permission="admin_role_delete">
                          <button className="role-card-btn danger" onClick={() => { setSelectedRoleId(safeRoleId); setDialogError(null); setOpenDeleteDialog(true); }}>
                            <DeleteIcon fontSize="small" /> حذف
                          </button>
                        </Gate>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <div className="users-pagination">
              <span className="pagination-info">
                {page * limit + 1} تا {Math.min((page + 1) * limit, totalItems)} از {totalItems}
              </span>
              <div className="pagination-controls">
                <select className="users-select" value={limit} onChange={e => { setLimit(+e.target.value); setPage(0); }}>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                </select>
                <button className="pagination-btn" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>
                  <ChevronLeft fontSize="small" />
                </button>
                <button className="pagination-btn" onClick={() => setPage(p => p - 1)} disabled={page === 0}>
                  <ChevronRight fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={openCreateDialog} onClose={() => !dialogLoading && setOpenCreateDialog(false)} maxWidth="md" fullWidth className="role-edit-dialog">
        <div className="edit-dialog-title">
          <span>افزودن نقش جدید</span>
          <button className="details-close" onClick={() => setOpenCreateDialog(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {dialogError && <div className="dialog-error">{dialogError}</div>}
          <div className="edit-form">
            {/* Basic Info Card */}
            <div className="edit-card">
              <div className="edit-card-header">اطلاعات اصلی</div>
              <div className="edit-card-body">
                <div className="edit-form-grid">
                  <div className="edit-field">
                    <label>نوع نقش</label>
                    <select
                      value={newRole.roleType}
                      onChange={(e) => setNewRole({ ...newRole, roleType: e.target.value as 'global' | 'tenant' })}
                    >
                      <option value="global">سراسری</option>
                      <option value="tenant">مختص تنانت</option>
                    </select>
                  </div>
                  {newRole.roleType === 'tenant' && (
                    <div className="edit-field">
                      <label>تنانت</label>
                      <select
                        value={newRole.tenantId || ''}
                        onChange={(e) => setNewRole({ ...newRole, tenantId: e.target.value })}
                      >
                        <option value="">انتخاب تنانت...</option>
                        {loadingTenants ? (
                          <option value="" disabled>در حال بارگذاری...</option>
                        ) : tenants.length === 0 ? (
                          <option value="" disabled>تنانتی یافت نشد</option>
                        ) : (
                          tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)
                        )}
                      </select>
                    </div>
                  )}
                  <div className="edit-field">
                    <label>نام نقش</label>
                    <input
                      type="text"
                      placeholder="مثلا: مدیر محصولات"
                      value={newRole.name}
                      onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                    />
                  </div>
                  <div className="edit-field full">
                    <label>توضیحات</label>
                    <textarea
                      placeholder="توضیح مختصر درباره این نقش..."
                      value={newRole.description}
                      onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Permissions Card — professional catalog-driven picker */}
            <div className="edit-card">
              <div className="edit-card-header">
                دسترسی‌ها
                {(newRole.permissions?.length || 0) > 0 && (
                  <span className="edit-card-badge">{newRole.permissions?.length} انتخاب شده</span>
                )}
              </div>
              <div className="edit-card-body">
                <RolePermissionPicker
                  picks={newRole.permissions ?? []}
                  onChange={(p) => setNewRole({ ...newRole, permissions: p })}
                />
                {renderExpansionPreview()}
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setOpenCreateDialog(false)} disabled={dialogLoading}>
            انصراف
          </button>
          <button
            className="edit-btn submit"
            onClick={handleCreateRole}
            disabled={dialogLoading || !newRole.name}
          >
            {dialogLoading ? <CircularProgress size={20} style={{ color: '#fff' }} /> : 'ذخیره'}
          </button>
        </div>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEditDialog} onClose={() => !dialogLoading && setOpenEditDialog(false)} maxWidth="md" fullWidth className="role-edit-dialog">
        <div className="edit-dialog-title">
          <span>ویرایش نقش</span>
          <button className="details-close" onClick={() => setOpenEditDialog(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {dialogError && <div className="dialog-error">{dialogError}</div>}
          {!editRole ? (
            <div className="perm-loading">اطلاعات نقش در دسترس نیست</div>
          ) : (
            <div className="edit-form">
              {/* Basic Info Card */}
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات اصلی</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>نوع نقش</label>
                      <select value={editRoleType} disabled>
                        <option value="global">سراسری</option>
                        <option value="tenant">مختص تنانت</option>
                      </select>
                    </div>
                    {editRoleType === 'tenant' && (
                      <div className="edit-field">
                        <label>تنانت</label>
                        <select value={editRoleTenantId || ''} disabled>
                          <option value="">-</option>
                          {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </div>
                    )}
                    <div className="edit-field">
                      <label>نام نقش</label>
                      <input
                        type="text"
                        value={editRole.name || ''}
                        onChange={(e) => setEditRole({ ...editRole, name: e.target.value })}
                        maxLength={100}
                      />
                      {(!editRole.name || editRole.name.trim() === '') && (
                        <span className="field-error">نام نقش الزامی است</span>
                      )}
                    </div>
                    <div className="edit-field full">
                      <label>توضیحات</label>
                      <textarea
                        value={editRole.description || ''}
                        onChange={(e) => setEditRole({ ...editRole, description: e.target.value })}
                        maxLength={500}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Permissions Card — professional catalog-driven picker */}
              <div className="edit-card">
                <div className="edit-card-header">
                  دسترسی‌ها
                  {(editRole.permissions?.length || 0) > 0 && (
                    <span className="edit-card-badge">{editRole.permissions.length} انتخاب شده</span>
                  )}
                </div>
                <div className="edit-card-body">
                  <RolePermissionPicker
                    picks={editRole.permissions ?? []}
                    onChange={(p) =>
                      setEditRole(editRole ? { ...editRole, permissions: p } : editRole)
                    }
                  />
                  {renderExpansionPreview()}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setOpenEditDialog(false)} disabled={dialogLoading}>
            انصراف
          </button>
          <button
            className="edit-btn submit"
            onClick={handleEditRole}
            disabled={dialogLoading || !editRole?.name || editRole.name.trim() === ''}
          >
            {dialogLoading ? <CircularProgress size={20} style={{ color: '#fff' }} /> : 'ذخیره تغییرات'}
          </button>
        </div>
      </Dialog>

      {/* Delete Dialog */}
      <ConfirmDialog
        open={openDeleteDialog}
        title="حذف نقش"
        message={
          <span>
            آیا از حذف نقش <strong>"{selectedRoleName}"</strong> اطمینان دارید؟
            <br />این عملیات غیرقابل بازگشت است.
          </span>
        }
        cancelText="انصراف"
        confirmText="حذف نقش"
        confirmColor="error"
        loading={dialogLoading}
        onCancel={() => setOpenDeleteDialog(false)}
        onConfirm={handleDeleteRole}
      >
        {dialogError && <div className="dialog-error">{dialogError}</div>}
      </ConfirmDialog>

      {/* View Permissions Dialog */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="sm" fullWidth className="role-edit-dialog">
        <div className="edit-dialog-title">
          <span>دسترسی‌های نقش: {viewRole?.name}</span>
          <button className="details-close" onClick={() => setOpenViewDialog(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {viewRole && (
            <div className="edit-form">
              <div className="edit-card">
                <div className="edit-card-header">
                  اطلاعات نقش
                </div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>نام نقش</label>
                      <input type="text" value={viewRole.name} disabled />
                    </div>
                    <div className="edit-field">
                      <label>نوع</label>
                      <input type="text" value={viewRole.tenantId ? 'مختص تنانت' : 'سراسری'} disabled />
                    </div>
                    {viewRole.description && (
                      <div className="edit-field full">
                        <label>توضیحات</label>
                        <input type="text" value={viewRole.description} disabled />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="edit-card">
                <div className="edit-card-header">
                  لیست دسترسی‌ها
                  <span className="edit-card-badge">{viewRole.permissions?.length || 0} دسترسی</span>
                </div>
                <div className="edit-card-body">
                  {viewRole.permissions?.length === 1 && viewRole.permissions[0] === '*' ? (
                    <span className="tag tag-sm tag-full-access">دسترسی کامل</span>
                  ) : viewRole.permissions?.length > 0 ? (
                    <div className="permissions-cell" style={{ flexWrap: 'wrap', gap: 6 }}>
                      {viewRole.permissions.map((perm, i) => (
                        <span key={i} className="tag tag-xs tag-permission">{perm}</span>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: 'var(--color-text-disabled)', fontSize: '0.875rem' }}>بدون دسترسی</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.type}>{snackbar.msg}</Alert>
      </Snackbar>
    </div>
  );
};

export default RoleManagement;
